// Diese Datei kapselt alle Backend-Aufrufe für den Foodosia-Produktkatalog
// (Standorte + Gerichte, öffentlich und im Admin-Editor). Die Seiten selbst
// (Speiseplan.jsx, AdminSpeiseplan.jsx) rufen nur die Funktionen hier auf und
// müssen sich nicht um Fetch/Auth/Fehlerbehandlung kümmern.
import { CATALOG_TYPE, LOCATION_ID } from "../data/mealPlan";
import { isFirebaseConfigured } from "./firebase";
import { getCurrentUserToken } from "./auth";

const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL?.trim() ||
  process.env.REACT_APP_API_BASE_URL?.trim() ||
  "/demo-local/us-central1/api";

const ADMIN_TOKEN_KEY = "foodosia-admin-token";
// Der lokale Emulator akzeptiert dieses "test:"-Format als Firebase-Token-Ersatz.
// Wird nur genutzt, wenn Firebase Auth nicht konfiguriert ist (siehe firebase.js).
const LOCAL_TEST_ADMIN_TOKEN =
  "test:uid=admin;role=super_admin;locations=loc-mannheim;verified=true";

function normalizeProduct(product) {
  // Das Backend kennt kein eigenes "tags"-Feld, nur Allergene. Daraus bauen wir die Badges.
  const tags = (product.allergens || []).map((allergen) => `${allergen} enthalten`);

  return {
    id: product.id,
    name: product.name,
    description: product.description || "Frisch zubereitet von Foodosia",
    priceCents: product.priceCents || 0,
    currency: product.currency || "EUR",
    tags,
    allergens: product.allergens || [],
    ingredients: product.ingredients || [],
    available: product.available !== false,
    locationIds: product.locationIds || [],
    imageUrl: product.imageUrl || product.image || "",
  };
}

function normalizeLocation(location) {
  return {
    id: location.id,
    name: location.name || location.city || location.id,
    address: location.address || location.description || "",
    active: location.active !== false,
    // Ob der Standort am Wochenende bedient wird - Standard ist zu, wie bei den
    // meisten Betriebskantinen (siehe validation.js im Backend für denselben Default).
    serveSaturday: Boolean(location.serveSaturday),
    serveSunday: Boolean(location.serveSunday),
  };
}

// Ist Firebase konfiguriert (siehe firebase.js), nutzen wir den echten ID-Token des
// eingeloggten Nutzers. Nur ohne Firebase-Konfiguration (lokale Entwicklung ohne die
// REACT_APP_FIREBASE_*-Variablen) greifen wir auf den Test-Token-Login zurück.
async function getAdminToken() {
  if (isFirebaseConfigured) {
    const token = await getCurrentUserToken();
    if (token) {
      return token;
    }
  }

  return (
    window.sessionStorage.getItem(ADMIN_TOKEN_KEY) ||
    process.env.REACT_APP_ADMIN_TOKEN ||
    LOCAL_TEST_ADMIN_TOKEN
  );
}

function buildProductPayload(dish, locationIds = []) {
  return {
    name: dish.name,
    description: dish.description,
    priceCents: dish.priceCents,
    currency: dish.currency || "EUR",
    allergens: dish.allergens || [],
    ingredients: dish.ingredients || [],
    available: dish.available !== false,
    locationIds,
    catalogType: CATALOG_TYPE,
  };
}

function buildLocationPayload(location) {
  return {
    name: location.name,
    address: location.address || "",
    active: location.active !== false,
    serveSaturday: Boolean(location.serveSaturday),
    serveSunday: Boolean(location.serveSunday),
  };
}

async function backendFetch(path, options = {}) {
  let response;

  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      cache: options.method && options.method !== "GET" ? undefined : "no-store",
      ...options,
    });
  } catch {
    throw new Error(`Backend nicht erreichbar: ${API_BASE_URL}`);
  }

  if (!response.ok) {
    const data = await response.json().catch(() => null);
    const error = new Error(data?.error?.message || "Anfrage fehlgeschlagen");
    error.status = response.status;
    throw error;
  }

  // Antworten ohne Inhalt (z.B. 204 No Content beim Löschen) haben bewusst keinen
  // JSON-Body - hier gibt es nichts zu parsen und keinen content-type zu prüfen.
  if (response.status === 204) {
    return null;
  }

  // Ohne laufendes Backend liefert der Dev-Server hier oft nur die index.html zurück.
  // Das wollen wir nicht stillschweigend als leere Liste behandeln.
  if (!response.headers.get("content-type")?.includes("application/json")) {
    throw new Error(
      `Keine gültige Antwort von ${API_BASE_URL} - REACT_APP_BACKEND_URL prüfen.`
    );
  }

  return response.json();
}

async function adminRequest(path, options = {}) {
  const token = await getAdminToken();

  return backendFetch(path, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
}

function toArray(data, key) {
  return Array.isArray(data) ? data : data[key] || [];
}

// Öffentliche Ansicht: Backend liefert hier bereits nur aktive Standorte.
export async function loadLocations() {
  const data = await backendFetch("/locations");
  return toArray(data, "locations").map(normalizeLocation);
}

// Öffentliche Ansicht: Backend liefert hier bereits nur available=true Produkte.
export async function loadMenuData(locationId = LOCATION_ID) {
  const data = await backendFetch(
    `/products?locationId=${locationId}&catalogType=${CATALOG_TYPE}`
  );
  return toArray(data, "products").map(normalizeProduct);
}

// Admin sieht eigentlich auch deaktivierte Standorte. Manche Backend-Stände kennen
// GET /admin/locations aber noch nicht - dann weichen wir auf die öffentliche Liste aus
// (zeigt dann nur aktive Standorte, aber der Editor bleibt nutzbar).
export async function loadAdminLocations() {
  try {
    const data = await adminRequest("/admin/locations");
    return toArray(data, "locations").map(normalizeLocation);
  } catch (error) {
    if (error.status === 404) {
      return loadLocations();
    }
    throw error;
  }
}

// Admin sieht eigentlich auch deaktivierte Produkte, siehe Kommentar bei loadAdminLocations.
export async function loadAdminProducts(locationId = LOCATION_ID) {
  try {
    const data = await adminRequest(
      `/admin/products?locationId=${locationId}&catalogType=${CATALOG_TYPE}`
    );
    return toArray(data, "products").map(normalizeProduct);
  } catch (error) {
    if (error.status === 404) {
      return loadMenuData(locationId);
    }
    throw error;
  }
}

export async function createProduct(dish, locationIds) {
  return adminRequest("/admin/products", {
    method: "POST",
    body: JSON.stringify(buildProductPayload(dish, locationIds)),
  });
}

export async function updateProduct(productId, dish, locationIds) {
  return adminRequest(`/admin/products/${productId}`, {
    method: "PATCH",
    body: JSON.stringify(buildProductPayload(dish, locationIds)),
  });
}

// Zusätzlich zum Aus-/Einblenden gibt es auch echtes, endgültiges Löschen (siehe
// deleteProduct unten) - z.B. für versehentlich angelegte oder doppelte Testeinträge.
export async function setProductAvailability(productId, available) {
  return adminRequest(`/admin/products/${productId}`, {
    method: "PATCH",
    body: JSON.stringify({ available }),
  });
}

// Löscht ein Produkt endgültig aus dem Katalog (kein "available: false" mehr,
// sondern echtes Entfernen). Die Bestätigung dafür holt die aufrufende UI ein.
export async function deleteProduct(productId) {
  return adminRequest(`/admin/products/${productId}`, {
    method: "DELETE",
  });
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // dataURL sieht so aus: "data:image/png;base64,AAAA..." - wir wollen nur den Teil danach.
      const base64 = String(reader.result).split(",")[1] || "";
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Bild konnte nicht gelesen werden."));
    reader.readAsDataURL(file);
  });
}

// Das Backend erwartet hier bewusst kein multipart/form-data, sondern normales JSON
// mit Base64-kodierten Bilddaten - deshalb wird die Datei vorher komplett gelesen
// und konvertiert, statt sie direkt als FormData zu verschicken.
export async function uploadProductImage(productId, file) {
  const base64Data = await readFileAsBase64(file);

  return adminRequest(`/admin/products/${productId}/image`, {
    method: "POST",
    body: JSON.stringify({
      fileName: file.name,
      contentType: file.type || "application/octet-stream",
      base64Data,
    }),
  });
}

export async function createLocation(location) {
  return adminRequest("/admin/locations", {
    method: "POST",
    body: JSON.stringify(buildLocationPayload(location)),
  });
}

export async function updateLocation(locationId, location) {
  return adminRequest(`/admin/locations/${locationId}`, {
    method: "PATCH",
    body: JSON.stringify(buildLocationPayload(location)),
  });
}

// Zusätzlich zum Aus-/Einblenden gibt es auch echtes, endgültiges Löschen (siehe
// deleteLocation unten) - z.B. für versehentlich angelegte Standorte.
export async function setLocationActive(locationId, active) {
  return adminRequest(`/admin/locations/${locationId}`, {
    method: "PATCH",
    body: JSON.stringify({ active }),
  });
}

// Löscht einen Standort endgültig (kein "active: false" mehr, sondern echtes
// Entfernen). Die Bestätigung dafür holt die aufrufende UI ein.
export async function deleteLocation(locationId) {
  return adminRequest(`/admin/locations/${locationId}`, {
    method: "DELETE",
  });
}

// Öffentlich lesbar (keine Anmeldung nötig): liefert { [datum]: { fixedId,
// special1Id, special2Id } } für einen Standort - welches Gericht an welchem Tag
// im 14-Tage-Speiseplan steht. Wird von Speiseplan.jsx UND AdminSpeiseplan.jsx
// gleichermaßen genutzt, siehe dailyMenuApi.js.
export async function loadDailyMenu(locationId) {
  const data = await backendFetch(`/daily-menu?locationId=${locationId}`);
  return data.slots || {};
}

// Speichert die komplette Zuordnung für genau einen Tag eines Standorts (ersetzt
// einen eventuell vorhandenen alten Eintrag für diesen Tag vollständig).
export async function saveDailyMenuSlot(locationId, date, slots) {
  return adminRequest(`/admin/locations/${locationId}/daily-menu/${date}`, {
    method: "PUT",
    body: JSON.stringify(slots),
  });
}
